import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { Fab, Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'

import i18n from '../../locales/i18n'
import { Nil } from '../../types'
import useFields from '../../utils/validation'
import SignatureDrawer, { SignatureDrawerHandle } from './SignatureDrawer'

const useStyles = makeStyles(
  theme => ({
    item: {
      width: '100%',
      padding: theme.spacing(0, 2),
      marginBottom: theme.spacing(2),
    },
    headerText: {
      fontSize: '2rem',
      color: theme.colors.primaryText,
      fontWeight: 500,
    },
    button: {
      width: 152,
      height: 40,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    skipButton: {
      marginRight: theme.spacing(1.5),
    },
    buttonContainer: {
      '&:not(:last-of-type)': {
        marginRight: theme.spacing(1.5),
      },
    },
  }),
  { name: 'BaseSignature' },
)

export interface BaseSignatureProps {
  // TODO PR-364: replace with ValidationField type
  readonly children?: (field: any) => React.ReactNode
  readonly initialSignatureUrl?: string | Nil
  readonly initialSignerValue?: string | Nil
  readonly isLoading?: boolean
  readonly onClose?: () => void
  readonly onSign: (signer: string, value: string) => void
  readonly outputFormat?: 'png' | 'svg'
  readonly showSkip?: boolean
  readonly type?: 'select' | 'text'
}

export interface BaseSignatureHandle {
  hasUnsavedChanges: () => boolean
  trySign: () => void
}

const BaseSignature = forwardRef<BaseSignatureHandle, BaseSignatureProps>(
  function BaseSignature(
    {
      type,
      initialSignerValue = '',
      initialSignatureUrl,
      isLoading,
      children,
      outputFormat = 'svg',
      onSign,
      onClose,
      showSkip = false,
    },
    ref,
  ) {
    const classes = useStyles()
    const drawerRef = useRef<SignatureDrawerHandle>(null)

    const {
      fields: { signer },
      validate,
    } = useFields([
      {
        name: 'signer',
        label: i18n.t('Common:SIGNATURE.SIGNER'),
        type,
        validators: ['required'],
        initialValue: initialSignerValue,
      },
    ])

    const trySign = () => {
      const valid = validate()

      if (!valid || !drawerRef.current?.validate?.()) {
        return
      }

      const formatter =
        outputFormat === 'svg'
          ? drawerRef.current.getSvg
          : drawerRef.current.getPng

      onSign(signer.value, formatter())
    }

    useImperativeHandle(ref, () => ({
      hasUnsavedChanges: () =>
        signer.value !== signer.initialValue ||
        drawerRef.current?.getIsValid() ||
        false,
      trySign,
    }))

    return (
      <Grid container direction="column">
        <Grid item className={classes.item}>
          <Typography className={classes.headerText}>
            {i18n.t<string>('Common:SIGNATURE.PLEASE_SIGN_BELOW')}
          </Typography>
        </Grid>
        <Grid item className={classes.item}>
          <SignatureDrawer
            initialDataUrl={initialSignatureUrl}
            ref={drawerRef}
          />
        </Grid>
        {children && (
          <Grid item className={classes.item}>
            {children({ field: signer })}
          </Grid>
        )}
        <Grid
          container
          item
          className={classes.item}
          justifyContent="space-between"
          wrap="nowrap"
        >
          <Grid item xs className={classes.buttonContainer} sm="auto">
            <Fab
              className={classes.button}
              color="primary"
              type="button"
              variant="extended"
              onClick={() => drawerRef.current?.clear()}
            >
              {i18n.t<string>('Common:CLEAR_ACTION')}
            </Fab>
          </Grid>
          <Grid item xs className={classes.buttonContainer} sm="auto">
            {showSkip && (
              <Fab
                className={classNames(classes.button, classes.skipButton)}
                color="primary"
                type="button"
                variant="extended"
                onClick={onClose}
              >
                {i18n.t<string>('Common:SKIP_ACTION')}
              </Fab>
            )}
            <Fab
              className={classes.button}
              color="inherit"
              disabled={isLoading}
              type="button"
              variant="extended"
              onClick={trySign}
            >
              {i18n.t<string>('Common:SIGNATURE.SIGN_ACTION')}
            </Fab>
          </Grid>
        </Grid>
      </Grid>
    )
  },
)

export default BaseSignature
